<template>
  <el-main class="shadow campaign-card">
    <el-card>
      <template #header>
        <div
          class="header-container"
          :class="deviceClass"
          @click.self="collapse"
        >
          <div class="campaign-title">
            <el-icon class="arrow" @click.self="collapse">
              <ArrowDownElementIcon v-if="closed" @click.self="collapse" />
              <ArrowLeftElementIcon v-else @click.self="collapse" />
            </el-icon>
            <span class="campaign-id" @click.self="collapse">
              {{ campaign.id }}.
            </span>
            <span class="campaign-name" @click.self="collapse">
              {{ campaign.name }}
            </span>
            <StreamIcon
              class="stream-icon"
              :stream-id="campaign.stream"
              @click.self="collapse"
            />
            <AdTypeLabel :type="campaign.type_id" @click.self="collapse" />
            <BannerSize
              v-if="AdType.Banner === campaign.type_id"
              :size="campaign.size"
              class="ml-1"
              @click.self="collapse"
            />
            <DeviceIcon
              v-for="id in campaign.device_ids"
              :key="id"
              :device-id="id"
              class="ml-1"
              @click.self="collapse"
            />
            <AvailableTrafficLabel
              v-if="campaign.max_traffic > 0"
              v-model="campaign.max_traffic"
            />
            <el-tag
              v-if="campaign.flat_deal"
              class="ml-1"
              type="danger"
              size="small"
            >
              Flat deal
            </el-tag>
          </div>
          <CampaignActions
            :campaign="campaign"
            :show-button="isStaff"
            :pulsating="pulsating"
            :creatives-ids="creativesIds"
          />
        </div>
      </template>
      <CreativesList
        v-if="creativesIds.length > 0"
        :creatives-ids="creativesIds"
        :show-delete="isStaff"
        class="creatives-list"
        :class="{ active: !closed, 'ml-2': isDesktop, 'ml-1': !isDesktop }"
      />
      <div v-else v-show="!closed" class="empty ml-3">No creatives yet</div>
    </el-card>
  </el-main>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { useCampaignsStore } from '@/apps/traforama/store/campaigns'
import useResize from '@/hooks/useResize'
import { AdType, IItem } from '@/api/types'
import StreamIcon from '@/components/StreamIcon.vue'
import CreativesList from './CreativesList.vue'
import CampaignActions from './CampaignActions.vue'
import BannerSize from '@/apps/traforama/components/BannerSize.vue'
import DeviceIcon from '@/apps/traforama/components/DeviceIcon.vue'
import AdTypeLabel from '@/apps/traforama/components/AdTypeLabel.vue'
import AvailableTrafficLabel from './AvailableTrafficLabel.vue'
import { useUserStore } from '@/store/modules/user'

export default defineComponent({
  name: 'CampaignRow',
  components: {
    CampaignActions,
    CreativesList,
    StreamIcon,
    BannerSize,
    DeviceIcon,
    AdTypeLabel,
    AvailableTrafficLabel
  },
  props: {
    campaignId: {
      type: String,
      required: true
    },
    pulsating: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const campaignsStore = useCampaignsStore()
    const userStore = useUserStore()
    const { isStaff } = userStore
    const { deviceClass, isDesktop } = useResize()

    const campaign = computed(() => {
      const campaign = campaignsStore.items[props.campaignId]
      return campaign?.attributes
    })

    const creativesIds = computed(() => {
      const campaign = campaignsStore.items[props.campaignId]
      if (campaign !== undefined) {
        const creatives = campaign.relationships?.creatures?.data || []
        return creatives.map((entry) => entry.id)
      }
      return []
    })

    const checked = computed({
      get: () => {
        return campaignsStore.selected[props.campaignId] !== undefined
      },
      set: (checked: boolean) => {
        campaignsStore.ToggleSelection(props.campaignId, checked)
      }
    })

    const closed = computed({
      get: () => {
        return campaignsStore.closed[props.campaignId] !== undefined
      },
      set: (closed: boolean) => {
        campaignsStore.ToggleClose({ id: props.campaignId, isClosed: closed })
      }
    })

    const collapse = () => {
      closed.value = !closed.value
    }

    return {
      campaign,
      creativesIds,
      deviceClass,
      checked,
      AdType,
      isStaff,
      collapse,
      closed,
      isDesktop
    }
  }
})
</script>

<style lang="scss" scoped>
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .mr-1 {
    margin-right: 1em;
  }
  &.mobile {
    flex-direction: column;
    align-items: flex-start;
  }
}

.campaign-id {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

.stream-icon {
  margin-left: 0.5em;
  margin-right: 0.5em;
  color: var(--el-text-color-secondary);
}

.campaign-card {
  padding: 4px 20px;

  :deep(.el-card) {
    &:hover {
      box-shadow: -4px 4px 9px -1px var(--el-color-primary-light-5);
    }
  }

  :deep(.el-card__header) {
    padding: 5px 20px 5px 0;
    .arrow {
      margin: 5px;
    }
  }

  :deep(.el-card__body) {
    padding: 0 5px;
  }

  .el-main {
    padding-bottom: 2px;
    padding-top: 2px;
  }

  :deep(.creatives-list) {
    transition: height 1s ease-out;
    opacity: 0;
    height: 0;
    overflow: hidden;
  }

  :deep(.creatives-list.active) {
    opacity: 1;
    height: auto;
    transition: height 1s;
  }

  :deep(.campaign-name) {
    margin-right: 0.5em;
    font-weight: 600;
    font-size: 14px;
  }

  .empty {
    padding: 5px;
    color: var(--el-text-color-secondary);
  }
}
</style>
